import { sanitizeHtml } from './sanitize';
import { mathSourceText } from './noteRichContent';

/**
 * Convert note HTML to clean plain text for the clipboard.
 *
 * Unlike a naive tag strip, this preserves list structure the way a human
 * reads it: ordered lists stay numbered (1. 2. 3.) and unordered lists use
 * bullets (•). That matters when pasting into a plain-text-only field that
 * can't render real lists — a numbered list must not silently become bullets.
 */
export function htmlToPlainText(html: string): string {
  const container = document.createElement('div');
  container.innerHTML = sanitizeHtml(html);
  // Code blocks are held aside while the whitespace below is tidied, because
  // in code the newlines and the indentation *are* the content: a shell
  // session collapsed to one line is no longer a shell session.
  const codeBlocks: string[] = [];
  return renderNode(container, codeBlocks)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\0(\d+)\0/g, (_match, index) => codeBlocks[Number(index)])
    .trim();
}

function renderNode(node: Node, codeBlocks: string[]): string {
  let result = '';
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      result += child.textContent || '';
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;

    const el = child as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const math = mathSourceText(el);

    if (math !== null) {
      result += math;
    } else if (tag === 'br') {
      result += '\n';
    } else if (tag === 'pre') {
      codeBlocks.push(el.textContent || '');
      result += `\n\0${codeBlocks.length - 1}\0\n\n`;
    } else if (tag === 'ol' || tag === 'ul') {
      const ordered = tag === 'ol';
      let index = 1;
      el.childNodes.forEach((li) => {
        if (
          li.nodeType === Node.ELEMENT_NODE &&
          (li as HTMLElement).tagName.toLowerCase() === 'li'
        ) {
          const marker = ordered ? `${index}. ` : '• ';
          index += 1;
          result += marker + renderNode(li as HTMLElement, codeBlocks).trim() + '\n';
        }
      });
      result += '\n';
    } else if (tag === 'p' || tag === 'div' || /^h[1-6]$/.test(tag)) {
      result += renderNode(el, codeBlocks).trim() + '\n\n';
    } else {
      result += renderNode(el, codeBlocks);
    }
  });
  return result;
}
